import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { ProjectAPIs } from "~/common/typescript/apis/ProjectAPIs";
import { Project_Base } from "~/common/typescript/models/Project";
import ProjectItem from "../ProjectItem/ProjectItem";
import { Project_getProjects_Request } from "~/common/typescript/apis/ProjectAPIs";
import { Input, Button, Pagination, Spin } from "antd";
import { FilterOutlined, SearchOutlined, PhoneOutlined } from "@ant-design/icons";
import { useAtom } from "jotai";
import FilterPopup from "./FilterPopup/FilterPopup";
import {
  phoneNumberSearchAtom,
  projectNameSearchAtom,
  filterPhoneNumberAtom,
  filterProjectNameAtom,
  showFilterPopupAtom,
} from "./store";

const AllProjects = () => {
  const [items, setItems] = useState<Project_Base[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);

  // Jotai atoms
  const [phoneNumber, setPhoneNumber] = useAtom(phoneNumberSearchAtom);
  const [filterPhoneNumber, setFilterPhoneNumber] = useAtom(filterPhoneNumberAtom);
  const [projectName, setProjectName] = useAtom(projectNameSearchAtom);
  const [filterProjectName, setFilterProjectName] = useAtom(filterProjectNameAtom);
  const [, setShowFilterPopup] = useAtom(showFilterPopupAtom);

  const handleSearch = () => {
    setPage(1);
    setFilterPhoneNumber(phoneNumber);
    setFilterProjectName(projectName);
  };

  useEffect(() => {
    setPage(1);
  }, [filterPhoneNumber, filterProjectName]);

  useEffect(() => {
    setLoading(true);
    const request: Project_getProjects_Request = {
      page: page,
      pageSize: pageSize,
      phone_number: filterPhoneNumber,
      project_name: filterProjectName,
    };
    ProjectAPIs.remote_getProjects(request)
      .then((response) => {
        setItems(response.results || []);
        setTotal(response.pagination?.total || 0);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, pageSize, filterPhoneNumber, filterProjectName]);

  return (
    <UI_Wrapper>
      <FilterPopup />

      <UI_Header>
        <UI_TitleRow>
          <UI_Title>Tất cả dự án (admin)</UI_Title>
          {total > 0 && <UI_Badge>{total}</UI_Badge>}
        </UI_TitleRow>

        <UI_DesktopFilterGroup>
          <Input
            prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />}
            value={phoneNumber}
            placeholder="Số điện thoại"
            allowClear
            onChange={(e) => setPhoneNumber(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 170 }}
            size="middle"
          />
          <Input
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            value={projectName}
            placeholder="Tên dự án"
            allowClear
            onChange={(e) => setProjectName(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 190 }}
            size="middle"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Tìm kiếm
          </Button>
        </UI_DesktopFilterGroup>

        <UI_MobileFilterButton>
          <Button
            icon={<FilterOutlined />}
            onClick={() => setShowFilterPopup(true)}
            size="middle"
          >
            Lọc
          </Button>
        </UI_MobileFilterButton>
      </UI_Header>

      <Spin spinning={loading}>
        <UI_List>
          {items.length > 0 &&
            items.map((project) => {
              return <ProjectItem key={project.id!} project={project} />;
            })}
        </UI_List>

        {!loading && items.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>
            Không tìm thấy dự án nào
          </div>
        )}
      </Spin>

      {total > 0 && (
        <UI_PaginationWrapper>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            }}
            showSizeChanger
            pageSizeOptions={['10', '20', '50']}
            showTotal={(total) => `Tổng cộng ${total} dự án`}
          />
        </UI_PaginationWrapper>
      )}
    </UI_Wrapper>
  );
};

export default memo(AllProjects);

const UI_Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
`;

const UI_Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const UI_TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_Title = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const UI_Badge = styled.span`
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const UI_DesktopFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UI_MobileFilterButton = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const UI_List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 10px;
`;

const UI_PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;